import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
import HmacSHA256 from 'crypto-js/hmac-sha256';

// reducer - state management
import accountReducer from 'store/accountReducer';
import { FETCH_MASKED_MFA_METHODS, FETCH_USER_INFO, LOGIN, LOGOUT, MFA_CHALLENGE, MFA_REQUIRED } from 'store/actions';

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';
import useIsp from '../hooks/useIsp';
import logger from '../utils/logger';

const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  isp: null,
  verificationToken: null,
  rememberUserAgent: false,
  maskedMfaMethods: []
};

const encode = (str) =>
  encodeURIComponent(str)
    .replace(/!/g, '%21') // Manually encode !
    .replace(/'/g, '%27') // Manually encode '
    .replace(/\(/g, '%28') // Manually encode (
    .replace(/\)/g, '%29') // Manually encode )
    .replace(/\*/g, '%2A') // Manually encode *
    .replace(/%20/g, '+');

function generateSignature({ querystring, username, password, secret, timestamp } = {}) {
  const usernameEncoded = encode(username);
  const passwordEncoded = encode(password);
  const body = `username=${usernameEncoded}&password=${passwordEncoded}`;
  const payload = `${querystring}\n${body}\n${timestamp}`;
  const signature = HmacSHA256(payload, secret).toString();
  logger.debug('Login signature', signature);
  return signature;
}

const verifyToken = (serviceToken) => {
  // if (!serviceToken) {
  //     return false;
  // }
  // const decoded = jwtDecode(serviceToken);
  // /**
  //  * Property 'exp' does not exist on type '<T = unknown>(token, options?: JwtDecodeOptions | undefined) => T'.
  //  */
  // return decoded.exp > Date.now() / 1000;
  return !!serviceToken;
};

const setSession = (serviceToken) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

const setSubdomain = (subdomain) => {
  if (subdomain) {
    localStorage.setItem('subdomain', subdomain);
    axios.defaults.headers['X-Synergy-Subdomain'] = subdomain;
  } else {
    localStorage.removeItem('subdomain');
    delete axios.defaults.headers['X-Synergy-Subdomain'];
  }
};

const storeLoginData = (access_token, subdomain, user_agent_id, secret) => {
  setSession(access_token);
  setSubdomain(subdomain);
  if (user_agent_id) {
    localStorage.setItem('userAgentId', user_agent_id);
  }
  if (secret) {
    localStorage.setItem('secret', secret);
  }
};

const removeAgentSecret = () => {
  localStorage.removeItem('userAgentId');
  localStorage.removeItem('secret');
};

const retrieveRole = (user, subdomain) => {
  let role = null;
  try {
    if (subdomain) {
      const isp = user.isps.find((isp) => isp.subdomain === subdomain);
      role = isp.role;
    } else {
      role = user.roles.find((role) => role.roleId.startsWith('GUARDIAN') || role.roleId.startsWith('FIRMWARE')).roleId;
    }
  } catch (error) {
    logger.error(error);
  }
  if (!role) {
    throw new Error('Access denied. Account Role is not found.');
  }
  return role;
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const { subdomain, accountType } = useIsp();
  const fetchUserProfile = async (type) => {
    const response = await axios.get(`/synergy/accounts/self?fields=ROLES&fields=ISPS&type=${type}`);
    return response.data;
  };

  const fetchIsp = async (type) => {
    if (!type) {
      return null;
    }
    const response = await axios.get(`/synergy/isps/self?type=${type}`);
    return response.data;
  };

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');
        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);
          setSubdomain(subdomain);
          const user = await fetchUserProfile(accountType);
          const isp = await fetchIsp(accountType);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user,
              isp,
              role: retrieveRole(user, subdomain)
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        logger.error(err);
        setSession(null);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, [accountType, subdomain]);

  const login = async (email, password, rememberUserAgent = false) => {
    try {
      if (!rememberUserAgent) {
        removeAgentSecret();
      }
      const userAgentId = window.localStorage.getItem('userAgentId');
      const secret = window.localStorage.getItem('secret');
      const timestamp = Math.floor(Date.now());
      const response = await axios.post(
        `/synergy/oauth/token?grant_type=password`,
        new URLSearchParams({
          username: email.toLowerCase(),
          password,
          client_id: import.meta.env.VITE_API_CLIENT_TYPE,
          client_secret: import.meta.env.VITE_API_CLIENT_SECRET
        }).toString(),
        {
          headers: {
            'content-type': 'application/x-www-form-urlencoded',
            Authorization: null,
            'X-Synergy-User-Agent-Id': userAgentId && secret ? userAgentId : null,
            'X-Synergy-Signature':
              userAgentId && secret
                ? generateSignature({
                    querystring: `grant_type=password`,
                    username: email,
                    password,
                    secret,
                    timestamp
                  })
                : null,
            'X-Synergy-Timestamp': userAgentId && secret ? timestamp : null
          }
        }
      );
      const { access_token } = response.data;
      storeLoginData(access_token, subdomain);

      const user = await fetchUserProfile(accountType);
      const isp = await fetchIsp(accountType);

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user,
          isp,
          role: retrieveRole(user, subdomain)
        }
      });
    } catch (error) {
      if (error.code === 'MFA_REQUIRED') {
        dispatch({
          type: MFA_REQUIRED,
          payload: {
            email: email,
            verificationToken: error?.parameters[0]?.value,
            rememberUserAgent
          }
        });
      } else if (error.code === 'INVALID_SIGNATURE') {
        removeAgentSecret();
      }
      throw error;
    }
  };

  const fetchMaskedMfaMethods = async (email) => {
    const response = await axios.get(`/synergy/accounts/${encodeURIComponent(email)}/masked-mfa-methods`);
    dispatch({
      type: FETCH_MASKED_MFA_METHODS,
      payload: {
        maskedMfaMethods: response.data || []
      }
    });
  };

  const mfaChallenge = async (method, token) => {
    await axios.post(
      '/synergy/oauth/challenge',
      {
        challengeType: 'push-code',
        communicationMethod: method,
        verificationToken: token
      },
      {
        headers: {
          Authorization: 'Basic ' + window.btoa(`${import.meta.env.VITE_API_CLIENT_TYPE}:${import.meta.env.VITE_API_CLIENT_SECRET}`)
        }
      }
    );

    dispatch({
      type: MFA_CHALLENGE,
      payload: {
        mfaMethod: method
      }
    });
  };

  const mfaValidate = async (token, otp, rememberUserAgent) => {
    const response = await axios.post(
      '/synergy/oauth/token?grant_type=http://linksyssmartwifi.com/grant/mfa-validate',
      new URLSearchParams({ verification_token: token, otp, remember_user_agent: rememberUserAgent }).toString(),
      {
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          Authorization: 'Basic ' + window.btoa(`${import.meta.env.VITE_API_CLIENT_TYPE}:${import.meta.env.VITE_API_CLIENT_SECRET}`)
        }
      }
    );
    const { access_token, user_agent_id, secret } = response.data;
    storeLoginData(access_token, subdomain, rememberUserAgent ? user_agent_id : null, rememberUserAgent ? secret : null);

    const user = await fetchUserProfile(accountType);
    const isp = await fetchIsp(accountType);

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user,
        isp,
        role: retrieveRole(user, subdomain)
      }
    });
  };

  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = async () => {
    const serviceToken = window.localStorage.getItem('serviceToken');
    await axios.post(
      '/synergy/oauth/revoke',
      {
        access_token: serviceToken
      },
      {
        headers: {
          'content-type': 'application/x-www-form-urlencoded'
        }
      }
    );
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const oAuthLogin = async (code, state) => {
    try {
      const response = await axios.post(`/synergy/oauth/providers/token?grant_type=authorization_code&code=${code}&state=${state}`);
      const { access_token } = response.data;
      setSession(access_token);
      setSubdomain(subdomain);

      const user = await fetchUserProfile(accountType);
      const isp = await fetchIsp(accountType);

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user,
          isp,
          role: retrieveRole(user, subdomain)
        }
      });
    } catch (error) {
      logger.error('OAuth Login error:', error);
      throw error;
    }
  };

  const updateAppStateUserProfile = async () => {
    try {
      const user = await fetchUserProfile(accountType);
      dispatch({
        type: FETCH_USER_INFO,
        payload: {
          user
        }
      });
    } catch (error) {
      logger.error('OAuth Login error:', error);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    logger.debug(email);
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        mfaChallenge,
        mfaValidate,
        fetchMaskedMfaMethods,
        register,
        oAuthLogin,
        resetPassword,
        updateProfile,
        updateAppStateUserProfile
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default JWTContext;
